<template>
    <FilterBase
        :title="title"
        :narrow="narrow"
    >
        <InputRangeTwoKnobbedExponentialOkWithInputs
            v-model="calculatedModel"
            :bounds="bounds"
            :debounce="500"
            :linear-percent="90"
            :max-linear="5_000"
            :steps="1_000_000"
            :value-formatter="formatter"
        />
    </FilterBase>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import type { GemsApi_StonePriceFilter } from '@/graphql/api'

import FilterBase from '@/components/ui/filter/filters/layout/FilterBase.vue'
import type { AppCurrency } from '@/plugins/currency'
import type { Bounds } from 'wucdbm-exponential-slider'
import type { InputRangeTwoKnobbedValueFormatter } from '@/components/ui/inputs/range/input-range-two-knobbed-props.ts'
import InputRangeTwoKnobbedExponentialOkWithInputs from '@/components/ui/inputs/range/InputRangeTwoKnobbedExponentialOkWithInputs.vue'
import { useCurrencyInput } from '@/plugins/currency-input.ts'
import { useMoneyFormatter } from '@/plugins/money-formatter.ts'

const props = withDefaults(
    defineProps<{
        modelValue: GemsApi_StonePriceFilter | null
        bounds: Bounds
        currency: AppCurrency
        title?: string
        narrow?: boolean
    }>(),
    {
        modelValue: null,
        title: undefined,
    }
)

const emit = defineEmits<{
    (e: 'update:modelValue', v: GemsApi_StonePriceFilter): void
}>()

const currencyInput = useCurrencyInput()

const calculatedModel = computed({
    get(): Bounds {
        return {
            min: Number(props.modelValue?.min?.amount || props.bounds.min),
            max: Number(props.modelValue?.max?.amount || props.bounds.max),
        }
    },
    set(v: Bounds) {
        emit('update:modelValue', {
            min: { amount: v.min.toString(), currency: currencyInput.value },
            max: { amount: v.max.toString(), currency: currencyInput.value },
        })
    },
})

const moneyFormatter = useMoneyFormatter()

const formatter: InputRangeTwoKnobbedValueFormatter = (value) => {
    return moneyFormatter({
        amount: value.toString(),
        currency: props.currency,
    })
}
</script>
